import React, { useEffect, useState/*, useCallback*/, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { format } from 'date-fns';
import { getSiloDeliveries, SiloDelivery } from '../../../services/ApiService';
import styles from './SiloDeliveries.module.css';

const SiloDeliveries: React.FC = () => {
  // const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [deliveries, setDeliveries] = useState<SiloDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<SiloDelivery | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<{ url: string; alt: string } | null>(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    phoneNumber: '',
    status: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const isMounted = useRef(false);
  const initialFetchDone = useRef(false);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const response = await getSiloDeliveries({
        page,
        limit: rowsPerPage,
        ...appliedFilters,
      });
      if (isMounted.current) {
        setDeliveries(response.data);
        setTotalCount(response.pagination.total);
        setError(null);
      }
    } catch (err) {
      if (isMounted.current) {
        setError('Failed to fetch silo deliveries');
        console.error('Error fetching silo deliveries:', err);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    
    // Only fetch on initial mount or when dependencies change
    if (!initialFetchDone.current || page !== 1 || appliedFilters !== filters) {
      fetchDeliveries();
      initialFetchDone.current = true;
    }
    
    return () => {
      isMounted.current = false;
    };
  }, [page, rowsPerPage, appliedFilters]);

  // const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
  //   setPage(value);
  // };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setFilters((prev) => ({
      ...prev,
      status: event.target.value,
    }));
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleRowClick = (delivery: SiloDelivery) => {
    setSelectedDelivery(delivery);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDelivery(null);
  };

  const handleFilterToggle = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleImageClick = (url: string | undefined, alt: string) => {
    if (url) {
      setEnlargedImage({ url, alt });
    }
  };

  const handleCloseEnlargedImage = () => {
    setEnlargedImage(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('SiloDeliveries.Title')}</h2>
      <Box className={styles.generalSection}>
        <Box mb={2}>
          <Button
            variant="contained"
            onClick={handleFilterToggle}
          >
            {isFilterOpen ? t('Filters.Close') : t('Filters.Open')}
          </Button>
        </Box>

        <Collapse in={isFilterOpen}>
          <Box component="form" onSubmit={handleFilterSubmit} mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t('General.Labels.StartDate')}
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t('General.Labels.EndDate')}
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t('SiloDeliveries.Labels.PhoneNumber')}
                  name="phoneNumber"
                  value={filters.phoneNumber}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('SiloDeliveries.Labels.Status')}</InputLabel>
                  <Select
                    name="status"
                    value={filters.status}
                    label={t('SiloDeliveries.Labels.Status')}
                    onChange={handleStatusChange}
                  >
                    <MenuItem value="">{t('General.Labels.All')}</MenuItem>
                    <MenuItem value="pending">{t('SiloDeliveries.Status.Pending')}</MenuItem>
                    <MenuItem value="completed">{t('SiloDeliveries.Status.Completed')}</MenuItem>
                    <MenuItem value="cancelled">{t('SiloDeliveries.Status.Cancelled')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" type="submit">
                  {t('Filters.Apply')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('SiloDeliveries.Labels.PortVisitNumber')}</TableCell>
                <TableCell>{t('SiloDeliveries.Labels.PhoneNumber')}</TableCell>
                <TableCell>{t('SiloDeliveries.Labels.EntranceTime')}</TableCell>
                <TableCell>{t('SiloDeliveries.Labels.ExitTime')}</TableCell>
                <TableCell>{t('SiloDeliveries.Labels.Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveries.map((delivery) => (
                <TableRow 
                  key={delivery._id}
                  hover
                  onClick={() => handleRowClick(delivery)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{delivery.portVisitNumber}</TableCell>
                  <TableCell>{delivery.phoneNumber}</TableCell>
                  <TableCell>
                    {format(new Date(delivery.entranceTimestamp), 'yyyy-MM-dd HH:mm:ss')}
                  </TableCell>
                  <TableCell>
                    {delivery.exitTimestamp
                      ? format(new Date(delivery.exitTimestamp), 'yyyy-MM-dd HH:mm:ss')
                      : '-'}
                  </TableCell>
                  <TableCell>{delivery.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={styles.paginationContainer}>
          <Box className={styles.rowsPerPage}>
            {t('Pagination.Labels.RowsPerPage')}:
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </Box>
          <Box className={styles.paginationControls}>
            <Button
              variant="contained"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              sx={{ minWidth: '100px' }}
            >
              {t('Pagination.Labels.PreviousPage')}
            </Button>
            <Box sx={{ minWidth: '150px', textAlign: 'center' }}>
              {t('Pagination.Labels.CurrentPage', { currentPage: page, totalPages: Math.ceil(totalCount / rowsPerPage) })}
            </Box>
            <Button
              variant="contained"
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(totalCount / rowsPerPage)}
              sx={{ minWidth: '100px' }}
            >
              {t('Pagination.Labels.NextPage')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        dir={i18n.language === 'he' ? 'rtl' : 'ltr'}
      >
        {selectedDelivery && (
          <>
            <DialogTitle>
              {t('SiloDeliveries.Labels.SiloDeliveryDetails')} - {t('SiloDeliveries.Labels.Visit')} #{selectedDelivery.portVisitNumber}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6">{t('SiloDeliveries.Labels.VisitInformation')}</Typography>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                {/* Details Section */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1"><strong>{t('SiloDeliveries.Labels.PhoneNumber')}:</strong> {selectedDelivery.phoneNumber}</Typography>
                        <Typography variant="subtitle1"><strong>{t('SiloDeliveries.Labels.Status')}:</strong> {selectedDelivery.status}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1"><strong>{t('SiloDeliveries.Labels.EntranceTime')}:</strong> {format(new Date(selectedDelivery.entranceTimestamp), 'yyyy-MM-dd HH:mm:ss')}</Typography>
                        {selectedDelivery.exitTimestamp && (
                          <Typography variant="subtitle1"><strong>{t('SiloDeliveries.Labels.ExitTime')}:</strong> {format(new Date(selectedDelivery.exitTimestamp), 'yyyy-MM-dd HH:mm:ss')}</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Images Section */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography variant="subtitle1" gutterBottom><strong>{t('SiloDeliveries.Labels.EntranceImage')}</strong></Typography>
                          <img 
                            src={selectedDelivery.entranceImageUrl} 
                            alt="Entrance" 
                            style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', cursor: 'pointer' }} 
                            onClick={() => selectedDelivery.entranceImageUrl && handleImageClick(selectedDelivery.entranceImageUrl, 'Entrance')}
                          />
                        </Box>
                      </Grid>
                      
                      {selectedDelivery.exitImageUrl && (
                        <Grid item xs={12} md={6}>
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography variant="subtitle1" gutterBottom><strong>{t('SiloDeliveries.Labels.ExitImage')}</strong></Typography>
                            <img 
                              src={selectedDelivery.exitImageUrl} 
                              alt="Exit" 
                              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', cursor: 'pointer' }} 
                              onClick={() => selectedDelivery.exitImageUrl && handleImageClick(selectedDelivery.exitImageUrl, 'Exit')}
                            />
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
                
                {selectedDelivery.deliveries && selectedDelivery.deliveries.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2 }}>{t('SiloDeliveries.Labels.SiloDeliveries')}</Typography>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    
                    {selectedDelivery.deliveries.map((delivery) => (
                      <Grid item xs={12} key={delivery.deliveryId}>
                        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
                          <Grid container spacing={2}>
                            {/* Silo Title */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle1"><strong>{t('SiloDeliveries.Labels.SiloNumber')} #{delivery.siloNumber}</strong></Typography>
                            </Grid>

                            {/* Silo Details */}
                            <Grid item xs={12}>
                              <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="body2"><strong>{t('SiloDeliveries.Labels.Status')}:</strong> {delivery.status}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="body2"><strong>{t('SiloDeliveries.Labels.StartTime')}:</strong> {format(new Date(delivery.startTimestamp), 'yyyy-MM-dd HH:mm:ss')}</Typography>
                                    {delivery.endTimestamp && (
                                      <Typography variant="body2"><strong>{t('SiloDeliveries.Labels.EndTime')}:</strong> {format(new Date(delivery.endTimestamp), 'yyyy-MM-dd HH:mm:ss')}</Typography>
                                    )}
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                            
                            {/* Silo Images */}
                            <Grid item xs={12}>
                              <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
                                <Grid container spacing={2}>
                                  {delivery.startImageUrl && (
                                    <Grid item xs={12} sm={6}>
                                      <Box display="flex" flexDirection="column" alignItems="center">
                                        <Typography variant="subtitle2" gutterBottom><strong>{t('SiloDeliveries.Labels.StartImage')}</strong></Typography>
                                        <img 
                                          src={delivery.startImageUrl} 
                                          alt={`${t('SiloDeliveries.Labels.SiloNumber')} ${delivery.siloNumber} ${t('SiloDeliveries.Labels.StartImage')}`} 
                                          style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }} 
                                          onClick={() => delivery.startImageUrl && handleImageClick(delivery.startImageUrl, `${t('SiloDeliveries.Labels.SiloNumber')} ${delivery.siloNumber} ${t('SiloDeliveries.Labels.StartImage')}`)}
                                        />
                                      </Box>
                                    </Grid>
                                  )}
                                  
                                  {delivery.endImageUrl && (
                                    <Grid item xs={12} sm={6}>
                                      <Box display="flex" flexDirection="column" alignItems="center">
                                        <Typography variant="subtitle2" gutterBottom><strong>{t('SiloDeliveries.Labels.EndImage')}</strong></Typography>
                                        <img 
                                          src={delivery.endImageUrl} 
                                          alt={`${t('SiloDeliveries.Labels.SiloNumber')} ${delivery.siloNumber} ${t('SiloDeliveries.Labels.EndImage')}`} 
                                          style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }} 
                                          onClick={() => delivery.endImageUrl && handleImageClick(delivery.endImageUrl, `${t('SiloDeliveries.Labels.SiloNumber')} ${delivery.siloNumber} ${t('SiloDeliveries.Labels.EndImage')}`)}
                                        />
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} variant="outlined" color="error">
                {t('General.Labels.Close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Enlarged Image Dialog */}
      <Dialog
        open={!!enlargedImage}
        onClose={handleCloseEnlargedImage}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{enlargedImage?.alt}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center">
            <img 
              src={enlargedImage?.url} 
              alt={enlargedImage?.alt}
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} 
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEnlargedImage} variant="outlined" color="error">
            {t('General.Labels.Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SiloDeliveries; 